import { useMemo, useState } from 'react';
import { nip19 } from 'nostr-tools';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/useToast';
import { useAuthor } from '@/hooks/useAuthor';
import type { NostrDocProvider } from '@/lib/doc/provider';
import type { DocMetadata } from '@/lib/doc/types';
import { sanitizeUrl, shortPubkey } from '@/lib/identity';

interface ShareDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  metadata: DocMetadata | undefined;
  provider: NostrDocProvider;
  isOwner: boolean;
  shareUrl: string;
}

type AddState =
  | { status: 'idle' }
  | { status: 'resolving' }
  | { status: 'error'; message: string };

/** Parse user input into either a pubkey or a NIP-05 identifier. */
function parseIdentifier(input: string): { kind: 'pubkey' | 'nip05'; value: string } | undefined {
  const t = input.trim();
  if (!t) return undefined;
  if (/^[0-9a-f]{64}$/i.test(t)) return { kind: 'pubkey', value: t.toLowerCase() };
  if (t.startsWith('npub1') || t.startsWith('nprofile1')) {
    try {
      const decoded = nip19.decode(t);
      if (decoded.type === 'npub') return { kind: 'pubkey', value: decoded.data };
      if (decoded.type === 'nprofile') return { kind: 'pubkey', value: decoded.data.pubkey };
    } catch {
      return undefined;
    }
  }
  if (/^[\w.+-]+@[\w-]+(\.[\w-]+)+$/.test(t)) return { kind: 'nip05', value: t };
  return undefined;
}

/** Resolve a NIP-05 identifier to a pubkey via the well-known endpoint. */
async function resolveNip05(identifier: string): Promise<string | undefined> {
  const [name, ...rest] = identifier.split('@');
  const domain = rest.join('@');
  if (!name || !domain) return undefined;
  const url = `https://${domain}/.well-known/nostr.json?name=${encodeURIComponent(name)}`;
  const fetchJson = async (target: string) => {
    const response = await fetch(target, { signal: AbortSignal.timeout(5000) });
    if (!response.ok) throw new Error('not ok');
    return await response.json() as { names?: Record<string, string> };
  };
  try {
    const json = await fetchJson(url);
    const pubkey = json.names?.[name];
    return /^[0-9a-f]{64}$/.test(pubkey ?? '') ? pubkey : undefined;
  } catch {
    // Retry through the CORS proxy (some NIP-05 hosts lack CORS headers).
    try {
      const json = await fetchJson(`https://proxy.shakespeare.diy/?url=${encodeURIComponent(url)}`);
      const pubkey = json.names?.[name];
      return /^[0-9a-f]{64}$/.test(pubkey ?? '') ? pubkey : undefined;
    } catch {
      return undefined;
    }
  }
}

export function ShareDialog({
  open,
  onOpenChange,
  metadata,
  provider,
  isOwner,
  shareUrl,
}: ShareDialogProps) {
  const { toast } = useToast();
  const [input, setInput] = useState('');
  const [addRole, setAddRole] = useState<'editor' | 'viewer'>('editor');
  const [addState, setAddState] = useState<AddState>({ status: 'idle' });
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const collaborators = useMemo(() => metadata?.collaborators ?? [], [metadata]);

  const addCollaborator = async () => {
    if (!metadata) return;
    const parsed = parseIdentifier(input);
    if (!parsed) {
      setAddState({ status: 'error', message: 'Enter a valid npub, public key, or NIP-05 address.' });
      return;
    }
    setAddState({ status: 'resolving' });
    let pubkey: string | undefined;
    if (parsed.kind === 'pubkey') {
      pubkey = parsed.value;
    } else {
      pubkey = await resolveNip05(parsed.value);
      if (!pubkey) {
        setAddState({ status: 'error', message: `Could not resolve ${parsed.value}. Check the address and try again.` });
        return;
      }
    }
    if (pubkey === metadata.ownerPubkey) {
      setAddState({ status: 'error', message: 'That is the document owner.' });
      return;
    }
    if (metadata.collaborators.some((c) => c.pubkey === pubkey)) {
      setAddState({ status: 'error', message: 'Already shared with this user.' });
      return;
    }

    const newPubkey = pubkey;
    setBusy(true);
    const ok = await provider.updateCollaborators((current) => [
      ...current,
      { pubkey: newPubkey, role: addRole },
    ]);
    setBusy(false);
    if (ok) {
      setInput('');
      setAddState({ status: 'idle' });
    } else {
      setAddState({ status: 'error', message: 'Sharing failed — check your connection and try again.' });
    }
  };

  const changeRole = async (pubkey: string, role: 'editor' | 'viewer') => {
    setBusy(true);
    await provider.updateCollaborators((current) =>
      current.map((c) => (c.pubkey === pubkey ? { ...c, role } : c)),
    );
    setBusy(false);
  };

  const removeCollaborator = async (pubkey: string) => {
    setBusy(true);
    await provider.updateCollaborators((current) => current.filter((c) => c.pubkey !== pubkey));
    setBusy(false);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({ title: 'Could not copy the link', variant: 'destructive' });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Share document</DialogTitle>
          <DialogDescription>
            {isOwner
              ? 'Invite Nostr users to edit or view this document.'
              : 'People with access to this document.'}
          </DialogDescription>
        </DialogHeader>

        {isOwner && (
          <div className="space-y-2">
            <Label htmlFor="share-identifier">Add people</Label>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Input
                id="share-identifier"
                value={input}
                onChange={(event) => {
                  setInput(event.target.value);
                  setAddState({ status: 'idle' });
                }}
                placeholder="npub…, public key, or name@domain.com"
                autoComplete="off"
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    void addCollaborator();
                  }
                }}
                aria-invalid={addState.status === 'error'}
              />
              <Select value={addRole} onValueChange={(value) => setAddRole(value as 'editor' | 'viewer')}>
                <SelectTrigger className="w-full sm:w-28" aria-label="Permission">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="editor">Editor</SelectItem>
                  <SelectItem value="viewer">Viewer</SelectItem>
                </SelectContent>
              </Select>
              <Button type="button" onClick={() => void addCollaborator()} disabled={addState.status === 'resolving' || busy}>
                {addState.status === 'resolving' ? 'Adding…' : 'Add'}
              </Button>
            </div>
            {addState.status === 'error' && (
              <p className="text-sm text-destructive" role="alert">{addState.message}</p>
            )}
          </div>
        )}

        <div className="space-y-1">
          <p className="text-sm font-medium text-muted-foreground">People with access</p>
          {metadata && <CollaboratorRow pubkey={metadata.ownerPubkey} role="owner" isOwner={isOwner} busy={busy} />}
          {collaborators.map((collaborator) => (
            <CollaboratorRow
              key={collaborator.pubkey}
              pubkey={collaborator.pubkey}
              role={collaborator.role}
              isOwner={isOwner}
              busy={busy}
              onRoleChange={(role) => void changeRole(collaborator.pubkey, role)}
              onRemove={() => void removeCollaborator(collaborator.pubkey)}
            />
          ))}
          {collaborators.length === 0 && (
            <p className="rounded-lg border border-dashed px-4 py-6 text-center text-sm text-muted-foreground">
              Not shared with anyone yet. Only you can access this document.
            </p>
          )}
        </div>

        <div className="space-y-2 rounded-lg border bg-muted/40 p-3">
          <p className="text-sm font-medium">Document link</p>
          <p className="break-all text-xs text-muted-foreground">{shareUrl}</p>
          <Button type="button" variant="outline" size="sm" onClick={() => void copyLink()} className="w-full sm:w-auto">
            {copied ? 'Copied!' : 'Copy link'}
          </Button>
        </div>

        <DialogFooter className="text-xs text-muted-foreground sm:justify-start">
          Documents sync over public Nostr relays. This MVP keeps content unencrypted —
          end-to-end encryption is planned.
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CollaboratorRow({
  pubkey,
  role,
  isOwner,
  busy,
  onRoleChange,
  onRemove,
}: {
  pubkey: string;
  role: 'owner' | 'editor' | 'viewer';
  isOwner: boolean;
  busy: boolean;
  onRoleChange?: (role: 'editor' | 'viewer') => void;
  onRemove?: () => void;
}) {
  const author = useAuthor(pubkey);
  const metadata = author.data?.metadata;
  const name = metadata?.display_name || metadata?.name || shortPubkey(pubkey);
  const picture = sanitizeUrl(metadata?.picture);

  return (
    <div className="flex items-center gap-3 rounded-lg px-2 py-1.5 hover:bg-muted/50">
      <Avatar className="h-9 w-9 shrink-0">
        {picture ? <AvatarImage src={picture} alt={name} /> : null}
        <AvatarFallback>{name.slice(0, 1).toUpperCase()}</AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        {author.isLoading ? (
          <Skeleton className="h-4 w-24" />
        ) : (
          <p className="truncate text-sm font-medium">{name}</p>
        )}
        <p className="truncate text-xs text-muted-foreground">{shortPubkey(pubkey)}</p>
      </div>
      {isOwner && role !== 'owner' ? (
        <div className="flex items-center gap-1">
          <Select value={role} onValueChange={(value) => onRoleChange?.(value as 'editor' | 'viewer')} disabled={busy}>
            <SelectTrigger size="sm" className="h-8 w-24" aria-label={`Role for ${name}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="editor">Editor</SelectItem>
              <SelectItem value="viewer">Viewer</SelectItem>
            </SelectContent>
          </Select>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground hover:text-destructive"
            onClick={onRemove}
            disabled={busy}
            aria-label={`Remove ${name}`}
          >
            ✕
          </Button>
        </div>
      ) : (
        <span className="rounded-full bg-muted px-2.5 py-1 text-xs font-medium capitalize text-muted-foreground">
          {role}
        </span>
      )}
    </div>
  );
}
